import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFocusScroll } from "@/lib/navigation";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NotifyMeDialog } from "@/components/NotifyMe";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, PackagePlus } from "lucide-react";
import { matchesProduct, productTitle } from "@/lib/productNaming";
import { dealerColorLabel, dealerLabel } from "@/lib/naming";
import { SampleRequestDialog } from "@/components/SampleRequestDialog";

// RC49-INV-001 Audit trail tab — dealer-safe movement history
function InventoryAudit() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dealer/inventory/audit").then((r) => setData(r.data)).catch(() => setData({ rows: [], error: true })); }, []);
  if (!data) return <div className="mt-4 flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-terracotta" /></div>;
  if (data.error) return <div className="mt-4 rounded-md border border-status-sold/40 bg-white p-6 text-sm text-status-sold" data-testid="inventory-audit-error">The audit trail could not be loaded. Please retry.</div>;
  if (!data.rows.length) return <div className="mt-4 rounded-md border border-dashed border-limestone bg-white p-10 text-center text-sm text-warmgrey" data-testid="inventory-audit-empty">No inventory movements have been recorded yet.</div>;
  return (
    <div className="mt-4 overflow-x-auto rounded-md border border-border bg-white" data-testid="inventory-audit-table">
      <table className="w-full min-w-[720px] text-sm">
        <thead><tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
          <th className="px-4 py-3">When</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Location</th><th className="px-4 py-3 text-right">Change</th><th className="px-4 py-3">Reason</th></tr></thead>
        <tbody>{data.rows.map((a) => (
          <tr key={a.id} className="border-b border-border" data-testid={`inventory-audit-row-${a.id}`}>
            <td className="px-4 py-2 text-xs text-warmgrey whitespace-nowrap">{new Date(a.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
            <td className="px-4 py-2"><p className="font-medium text-charcoal">{a.product_title}</p><p className="text-xs text-warmgrey">{a.sku}</p></td>
            <td className="px-4 py-2 text-xs">{a.location} · {a.pool}/{a.field}</td>
            <td className="px-4 py-2 text-right num whitespace-nowrap">{a.qty_delta > 0 ? "+" : ""}{a.qty_delta} boxes <span className="text-xs text-warmgrey">({a.old_qty}→{a.new_qty})</span></td>
            <td className="px-4 py-2 text-xs text-charcoal">{(a.reason || "").replaceAll("_", " ").toLowerCase()}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

const STATE_LABEL = {
  IN_STOCK: "In Stock",
  LIMITED: "Limited",
  INCOMING: "Incoming",
  AT_ORIGIN: "At Origin",
  NEEDS_SOURCING: "Needs Sourcing",
  UNAVAILABLE: "Unavailable",
};
const STATE_BADGE = {
  IN_STOCK: "badge-available",
  LIMITED: "badge-reserved",
  INCOMING: "badge-reserved",
  AT_ORIGIN: "badge-committed",
  NEEDS_SOURCING: "badge-committed",
  UNAVAILABLE: "badge-sold",
};
const fmt$ = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

export default function DealerInventory() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(["morocco", "transit", "florida", "audit"].includes(params.get("tab")) ? params.get("tab") : "morocco");
  // ID-081: Shipments → "View incoming inventory" narrows this page to that shipment's products (existing rows, no new calculation)
  const shipRef = params.get("shipment");
  const shipSkus = shipRef && params.get("skus") ? new Set(params.get("skus").split(",")) : null;
  const clearShipment = () => { const p = new URLSearchParams(params); ["shipment", "skus"].forEach((k) => p.delete(k)); setParams(p, { replace: true }); };
  const [avail, setAvail] = useState("all");
  const [srcForm, setSrcForm] = useState(null); // {variant_id, label, boxes, requested_delivery_date, note}
  const [reserveForm, setReserveForm] = useState(null); // {variant_id, label, boxes, project_id, max}
  const [addProjectForm, setAddProjectForm] = useState(null); // {variant_id, label, project_id}
  const [projects, setProjects] = useState([]);
  const [sampleFor, setSampleFor] = useState(null); // RC49-SMP-001/004: variant_id from product context
  const [catalog, setCatalog] = useState([]);
  const openSample = async (variantId) => {
    if (!catalog.length) { try { const { data } = await api.get("/catalog"); setCatalog(data); } catch { toast.error("Could not load the catalog for the sample request"); return; } }
    await loadProjects();
    setSampleFor(variantId);
  };
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dealer/inventory");
      setRows(data.rows);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // RC48-ACT-003/005/006: Copilot deep links land on the exact row and open its workflow
  useEffect(() => {
    const pq = params.get("q");
    if (pq) setQ(pq);
    const action = params.get("action");
    if (!rows || !pq || !action) return;
    const r = rows.find((x) => x.sku === pq);
    if (!r) return;
    if (action === "add-project") { setAddProjectForm({ variant_id: r.variant_id, label: r.label, project_id: "" }); loadProjects(); }
    if (action === "sourcing") setSrcForm({ id: r.variant_id, label: r.label, sku: r.sku, box_coverage_sqft: r.box_coverage_sqft });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, params]);

  const submitSourcing = null; // ID-079: replaced by the shared Request Availability dialog
  const _unusedSourcing = async () => {
    setBusy(true);
    try {
      await api.post("/dealer/sourcing-requests", {
        variant_id: srcForm.variant_id,
        ...(srcForm.basis === "sqft" ? { sqft: parseFloat(srcForm.sqft || "0") } : { boxes: parseInt(srcForm.boxes || "0", 10) }),
        requested_delivery_date: srcForm.requested_delivery_date || null,
        note: srcForm.note || "",
      });
      toast.success("Sourcing request sent — Zelligio will evaluate stock, incoming shipments and origin supply");
      setSrcForm(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const loadProjects = async () => {
    if (projects.length) return;
    try { const { data } = await api.get("/projects"); setProjects(data); } catch { /* non-blocking */ }
  };

  const submitReserve = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/reservations", {
        variant_id: reserveForm.variant_id,
        boxes: parseInt(reserveForm.boxes || "0", 10),
        project_id: reserveForm.project_id || null,
        ...(reserveForm.container_id ? { container_id: reserveForm.container_id } : {}),
      });
      const pending = data?.status === "PENDING_APPROVAL";
      const hrs = data.hold_hours || 72;
      toast.success(pending
        ? `Reservation submitted — ${data.boxes} boxes await Zelligio approval (repeat hold)`
        : `Reservation confirmed — ${data.boxes} boxes held for ${hrs >= 48 ? `${Math.round(hrs / 24)} days` : `${hrs}h`}`, {
        action: { label: "Open reservation", onClick: () => navigate(`/dealer/reservations?focus=${data.id}`) },
      });
      setReserveForm(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitAddToProject = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${addProjectForm.project_id}/lines`, { variant_id: addProjectForm.variant_id });
      toast.success("Added to the project — material, boxes and pricing are calculated there");
      setAddProjectForm(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // V5-D NAV-004: Copilot deep link ?q=SKU → exact row focused (scroll + highlight), not just filtered
  const focusSku = params.get("q") || null;
  const focusRow = focusSku && rows ? rows.find((r) => r.sku === focusSku) : null;
  useFocusScroll(focusRow ? `inventory-row-${focusRow.sku}` : null, !!focusRow);
  if (!rows) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="dealer-inventory-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  const needle = q.trim();

  const inTab = (r) => tab === "florida" ? true : tab === "transit" ? r.incoming_boxes > 0 : tab === "morocco" ? r.origin_boxes > 0 : true;
  // V5.1: the Reserve action targets the supply location of the active tab (Florida 72h · Morocco / In Transit 7 days)
  const openReserve = (r) => {
    const stage = tab === "transit" ? "IN_TRANSIT" : tab === "morocco" ? "MOROCCO" : null;
    const opts = stage ? (r.supply_options || []).filter((o) => o.stage === stage) : [];
    const opt = opts[0] || null;
    const max = stage ? (opt?.available || 0) : r.available_boxes;
    setReserveForm({ variant_id: r.variant_id, label: r.label, boxes: "", project_id: "", max, stage: stage || "FLORIDA",
      container_id: opt?.container_id || null, zc_ref: opt?.zc_ref || null, options: opts });
    loadProjects();
  };
  const canReserveHere = (r) => tab === "florida" ? r.available_boxes > 0 && r.reservable
    : (r.supply_options || []).some((o) => o.stage === (tab === "transit" ? "IN_TRANSIT" : "MOROCCO") && o.available > 0);
  const LOCATION_BORDER = { morocco: "#D8D5CE", transit: "#B86F54", florida: "#96A58F", audit: "#e7e2da" };
  const LOCATION_LABEL = { morocco: "Morocco", transit: "In Transit", florida: "Florida" };
  // RC49-INV-002: availability filter is scoped inside the selected supply-state view
  const availOk = (r) => avail === "all" ? true : avail === "available" ? (tab === "transit" ? r.incoming_boxes > 0 : tab === "morocco" ? r.origin_boxes > 0 : r.available_boxes > 0)
    : avail === "limited" ? r.state === "LIMITED" : avail === "needs_sourcing" ? r.available_boxes === 0 : true;
  const filtered = rows.filter((r) => inTab(r) && availOk(r) && matchesProduct(needle, r) && (!shipSkus || shipSkus.has(r.sku)));  // RC49-INV-005/006, GOV-004
  const EMPTY = { florida: "No Florida inventory matches your current filters.", transit: "No in-transit inventory matches your current filters.",
    morocco: "No Morocco inventory matches your current filters.", audit: "No inventory movements have been recorded yet." };
  const TABS = [["morocco", "Morocco"], ["transit", "In Transit"], ["florida", "Florida"], ["audit", "Audit trail"]];

  return (
    <div data-testid="dealer-inventory-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Commercial</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Inventory</h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        What you can sell now, what is on the way, and what needs sourcing. Available quantities are commercial
        availability — stock already reserved or committed to other orders is not shown as sellable. If Florida stock
        cannot fulfill your need, request sourcing instead of walking away.
      </p>

      {/* RC49-INV-001/004: four supply-state views; horizontally scrollable segmented control on mobile */}
      {shipSkus && (
        <p className="mt-4 inline-flex flex-wrap items-center gap-2 rounded-full border border-terracotta/40 bg-terracotta/5 px-3 py-1 text-xs text-charcoal" data-testid="inventory-shipment-filter">
          Showing the {shipSkus.size} product{shipSkus.size === 1 ? "" : "s"} in shipment <span className="font-mono">{shipRef}</span>
          <Link to="/dealer/shipments" className="text-terracotta underline" data-testid="inventory-shipment-track">Track shipment</Link>
          <button type="button" onClick={clearShipment} className="text-warmgrey underline" data-testid="inventory-shipment-clear">Show all products</button>
        </p>
      )}
      <div className="mt-5 -mx-1 overflow-x-auto px-1 pb-1" data-testid="inventory-tabs">
        <div className="inline-flex min-w-max rounded-full border border-border bg-white p-1">
          {TABS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`inventory-tab-${k}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-terracotta text-white" : "text-charcoal hover:bg-sand"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab !== "audit" && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU, color code, U.S. or factory color, Mzedej / Bejmat / Mdebdeb, 4×4 / 2×6 / 2×2"
            className="h-10 bg-white sm:max-w-md" data-testid="inventory-search" />
          <select value={avail} onChange={(e) => setAvail(e.target.value)} className="h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="inventory-availability-filter">
            <option value="all">All availability</option>
            <option value="available">{tab === "transit" ? "Has in-transit quantity" : tab === "morocco" ? "Has Morocco quantity" : "Available now"}</option>
            {tab === "florida" && <option value="limited">Limited</option>}
            {tab === "florida" && <option value="needs_sourcing">Needs sourcing</option>}
          </select>
          <span className="text-xs text-warmgrey" data-testid="inventory-result-count">{filtered.length} of {rows.filter(inTab).length}</span>
        </div>
      )}

      {tab === "audit" ? <InventoryAudit /> : filtered.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-limestone bg-white p-10 text-center text-sm text-warmgrey" data-testid="inventory-empty">
          {focusSku && !focusRow ? <span data-testid="inventory-focus-missing">No authorized inventory record matches “{focusSku}”. It may be inactive or outside your scope.</span>
            : needle ? `No inventory matches “${needle}” in ${TABS.find(([k]) => k === tab)[1]}.` : EMPTY[tab]}
        </div>
      ) : (
      <div className="mt-4 overflow-x-auto rounded-md border-2 bg-white" style={{ borderColor: LOCATION_BORDER[tab] }} data-testid="inventory-table-scroll">
        <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-widest text-charcoal" data-testid={`inventory-location-label-${tab}`}>
          {LOCATION_LABEL[tab]} · {tab === "florida" ? "available now · 72-hour hold" : "pre-reservation · 7-day hold · 30% deposit to commit"}
        </p>
        <table className="w-full min-w-[980px] text-sm" data-testid="dealer-inventory-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="sticky left-0 z-10 bg-white px-4 py-3">Product</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Available now</th><th className="px-4 py-3 text-right">Incoming</th>
              <th className="px-4 py-3 text-right">At origin</th><th className="px-4 py-3 text-right">Your price</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.variant_id} id={`inventory-row-${r.sku}`} className={`border-b border-border align-top ${focusRow && focusRow.sku === r.sku ? "bg-terracotta/5 ring-2 ring-inset ring-terracotta" : ""}`}
                data-testid={`inventory-row-${r.sku}`} data-focused={focusRow && focusRow.sku === r.sku ? "true" : undefined}>
                <td className="sticky left-0 z-10 bg-white px-4 py-3 shadow-[1px_0_0_0_#e7e2da]">
                  {/* RC49-INV-007 / GOV-003/005/006: U.S. name first, factory name second, canonical metadata */}
                  <p className="font-semibold text-terracotta" data-testid={`inventory-us-name-${r.sku}`}>{dealerColorLabel(r)}</p>
                  <p className="text-xs text-charcoal" data-testid={`inventory-title-${r.sku}`}>{r.dealer_label || dealerLabel(r) || productTitle(r)}</p>
                  <p className="text-xs text-warmgrey">{r.sku}{r.color_code ? ` · ${r.color_code}` : ""} · <span className="whitespace-nowrap">{r.box_coverage_sqft} sq ft/box</span>
                    {r.sqft_per_sheet != null && <span className="whitespace-nowrap" data-testid={`inventory-sheet-${r.sku}`}> · {r.sqft_per_sheet} sq ft/sheet · {r.sheets_per_box ? `${r.sheets_per_box} sheets/box` : "sheets/box not set"}</span>}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATE_BADGE[r.state]}`}
                    data-testid={`inventory-state-${r.sku}`}>{STATE_LABEL[r.state]}</span>
                  {r.sourcing_open > 0 && <p className="mt-1 text-[10px] text-warmgrey">Sourcing requested ({r.sourcing_open})</p>}
                </td>
                <td className="px-4 py-3 text-right num text-charcoal" data-testid={`inventory-available-${r.sku}`}>
                  {r.available_boxes} boxes
                  <p className="text-xs text-warmgrey">{r.available_sqft} sq ft</p>
                </td>
                <td className="px-4 py-3 text-right num text-charcoal" data-testid={`inventory-incoming-${r.sku}`}>
                  {r.incoming_boxes > 0 ? (
                    <>
                      {r.incoming_boxes} boxes
                      <p className="text-xs text-warmgrey">
                        {r.next_eta ? `Est. warehouse availability ${new Date(`${String(r.next_eta).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : r.eta_overdue ? "Warehouse availability date passed — arrival unconfirmed" : "Warehouse availability date to be confirmed"}
                        {r.incoming_stage_label && <Link to="/dealer/shipments" className="block text-terracotta underline-offset-2 hover:underline" data-testid={`inventory-stage-${r.sku}`}>{r.incoming_stage_label}</Link>}
                        {r.pre_reservable_boxes > 0 && ` · ${r.pre_reservable_boxes} pre-reservable`}
                      </p>
                    </>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-right num text-charcoal" data-testid={`inventory-origin-${r.sku}`}>
                  {r.origin_boxes > 0 ? `${r.origin_boxes} boxes` : "—"}
                </td>
                <td className="px-4 py-3 text-right num text-charcoal" data-testid={`inventory-price-${r.sku}`}>
                  {r.buy_price_per_box != null ? (
                    <Link to={r.pricing_link} className="hover:text-terracotta hover:underline" data-testid={`inventory-pricing-link-${r.sku}`}>
                      {fmt$(r.buy_price_per_box)}/box
                      {r.resale_per_sqft != null && <p className="text-xs text-warmgrey">sells {fmt$(r.resale_per_sqft)}/sq ft · {r.margin_pct}% margin</p>}
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {/* RC48-INV-016/017 + UI-003/004/005: Reserve and Add to Project resolve
                        to real server outcomes; Reserve is visibly disabled with an
                        explanation when nothing is reservable — never a silent no-op. */}
                    {(() => {
                      const reservable = canReserveHere(r);
                      return (
                        <button type="button" disabled={!reservable}
                          title={reservable ? "Hold this inventory while your customer decides" : (r.reserve_block_reason || "Nothing available to reserve at this location")}
                          onClick={() => openReserve(r)}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${reservable ? "border-terracotta text-terracotta hover:bg-terracotta hover:text-white" : "cursor-not-allowed border-limestone text-warmgrey opacity-60"}`}
                          data-testid={`inventory-reserve-${r.sku}`}>Reserve</button>
                      );
                    })()}
                    <button type="button" onClick={() => { setAddProjectForm({ variant_id: r.variant_id, label: r.label, project_id: "" }); loadProjects(); }}
                      className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal hover:border-terracotta hover:text-terracotta"
                      data-testid={`inventory-add-project-${r.sku}`}>Add to project</button>
                    <Link to={`/dealer/discover`} className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal hover:border-terracotta hover:text-terracotta" data-testid={`inventory-view-product-${r.sku}`}>View product</Link>
                    <button type="button" onClick={() => openSample(r.variant_id)} className="rounded-full border border-limestone px-2.5 py-1 text-[11px] font-medium text-charcoal hover:border-terracotta hover:text-terracotta" data-testid={`inventory-sample-${r.sku}`}>Request sample</button>
                    <button type="button" onClick={() => setSrcForm({ id: r.variant_id, label: r.label, sku: r.sku, box_coverage_sqft: r.box_coverage_sqft })}
                      className="rounded-full border border-terracotta px-2.5 py-1 text-[11px] font-medium text-terracotta hover:bg-terracotta hover:text-white"
                      data-testid={`inventory-request-sourcing-${r.sku}`}>
                      <PackagePlus className="mr-1 inline h-3 w-3" />Request Availability
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <Dialog open={!!reserveForm} onOpenChange={(o) => !o && setReserveForm(null)}>
        <DialogContent className="max-w-md border-border bg-white" data-testid="reserve-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Reserve — {reserveForm?.label}</DialogTitle></DialogHeader>
          {reserveForm && (
            <div className="space-y-3">
              <p className="text-xs text-warmgrey" data-testid="reserve-policy-note">
                {reserveForm.stage === "FLORIDA"
                  ? "Florida stock is held for 72 hours. Complete a paid order within that time or the hold is released. Repeat holds on the same product and project need Zelligio approval."
                  : `${reserveForm.stage === "MOROCCO" ? "Morocco" : "In-transit"} supply${reserveForm.zc_ref ? ` · ${reserveForm.zc_ref}` : ""} is pre-reserved for 7 days. A 30% deposit commits the allocation; the balance is due before Florida dispatch. A released unpaid hold cannot be repeated on this container until it reaches Florida.`}
              </p>
              {reserveForm.options?.length > 1 && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Container</label>
                  <select value={reserveForm.container_id || ""} data-testid="reserve-container"
                    onChange={(e) => { const o = reserveForm.options.find((x) => x.container_id === e.target.value); setReserveForm({ ...reserveForm, container_id: o.container_id, zc_ref: o.zc_ref, max: o.available }); }}
                    className="mt-1 h-10 w-full rounded-md border border-border bg-white px-3 text-sm">
                    {reserveForm.options.map((o) => <option key={o.container_id} value={o.container_id}>{o.zc_ref} · {o.available} boxes{o.eta ? ` · ETA ${o.eta}` : ""}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Boxes (up to {reserveForm.max})</label>
                <Input type="number" min="1" max={reserveForm.max} value={reserveForm.boxes} onChange={(e) => setReserveForm({ ...reserveForm, boxes: e.target.value })} className="mt-1 h-10 bg-white" data-testid="reserve-boxes" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-warmgrey">Project (optional)</label>
                <Select value={reserveForm.project_id} onValueChange={(v) => setReserveForm({ ...reserveForm, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-10 bg-white" data-testid="reserve-project"><SelectValue placeholder="No project" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {parseInt(reserveForm.boxes || "0", 10) > reserveForm.max && (
                <p className="text-xs text-status-sold" data-testid="reserve-over-max">Only {reserveForm.max} boxes are available to reserve. Request sourcing for the remainder.</p>
              )}
              <Button onClick={submitReserve} disabled={busy || !reserveForm.boxes || parseInt(reserveForm.boxes, 10) < 1 || parseInt(reserveForm.boxes, 10) > reserveForm.max} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="reserve-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Request reservation
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!addProjectForm} onOpenChange={(o) => !o && setAddProjectForm(null)}>
        <DialogContent className="max-w-md border-border bg-white" data-testid="add-project-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Add to project — {addProjectForm?.label}</DialogTitle></DialogHeader>
          {addProjectForm && (
            <div className="space-y-3">
              <p className="text-xs text-warmgrey">The project calculates required material, whole boxes, pricing and availability for this product.</p>
              <Select value={addProjectForm.project_id} onValueChange={(v) => setAddProjectForm({ ...addProjectForm, project_id: v })}>
                <SelectTrigger className="h-10 bg-white" data-testid="add-project-select"><SelectValue placeholder="Choose a project" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {projects.length === 0 && <p className="text-xs text-warmgrey">No projects yet — create one from the Projects page first.</p>}
              <Button onClick={submitAddToProject} disabled={busy || !addProjectForm.project_id} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid="add-project-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add to project
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <NotifyMeDialog variant={srcForm} open={!!srcForm} onOpenChange={(o) => !o && setSrcForm(null)} projects={projects} onSubmitted={() => load()} />
      <SampleRequestDialog open={!!sampleFor} onOpenChange={(o) => { if (!o) setSampleFor(null); }} catalog={catalog} projects={projects}
        preselectedVariantId={sampleFor || ""} context="Inventory" onSubmitted={() => setSampleFor(null)} />

    </div>
  );
}
